﻿import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type AssignmentAnomalyUnit,
  type AuditRow,
  type FaciliooContractLiveResult,
  type FaciliooMasterDataProbeResult,
  type FaciliooProcessResolveResult
} from "../app/api";
import { ServerDataTable } from "../components/tables/ServerDataTable";

const faciliooSyncStatusLabel = (action: unknown) => (toText(action).endsWith("_failed") ? "Fehler" : "OK");

const faciliooSyncTrigger = (action: unknown) => {
  const normalized = toText(action);
  if (normalized.includes("_auto_")) return "Automatisch";
  if (normalized.includes("_manual_")) return "Manuell";
  return "Unbekannt";
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value: unknown): string => String(value ?? "").trim();

const toListText = (value: unknown): string => {
  if (!Array.isArray(value)) return "-";
  const items = value.map((entry) => toText(entry)).filter(Boolean);
  if (items.length === 0) return "-";
  if (items.length <= 2) return items.join(" | ");
  return `${items.slice(0, 2).join(" | ")} (+${items.length - 2})`;
};

type MissingOwnerContactListRow = {
  partyId: string;
  objectNumbers: string[];
  unitNumbers: string[];
  ownerNames: string[];
  contractIds: string[];
  partyContactStatus: string;
  accountResolutionStatus: string;
};

type AssignmentAnomalyNoActiveRow = {
  objektnummer: string;
  einheitsnummer: string;
};

type AssignmentAnomalyNoActiveReasonRow = {
  reason: string;
  count: number;
};

type AssignmentAnomalyNoActiveDetailRow = {
  objektnummer: string;
  einheitsnummer: string;
  details: string;
};

type AssignmentAnomalyNoActiveContractProbeRow = {
  objektnummer: string;
  einheitsnummer: string;
  eigentuemerId: string;
  eigentuemername: string;
  assignmentId: string;
  gueltigAb: string;
  gueltigBis: string;
  status: string;
  reason: string;
};

const formatDurationLabel = (durationMs?: number) => {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs <= 0) return "-";
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
};

type AssignmentAnomalyDebugListProps = {
  id: string;
  title: string;
  units: AssignmentAnomalyUnit[];
  csvFilenamePrefix: string;
  emptyText: string;
  ignoredInfoCount?: number;
  hint: (unit: AssignmentAnomalyUnit) => string;
};

const AssignmentAnomalyDebugList = ({
  id,
  title,
  units,
  csvFilenamePrefix,
  emptyText,
  ignoredInfoCount = 0,
  hint
}: AssignmentAnomalyDebugListProps) => {
  const [search, setSearch] = useState("");
  const [objectFilter, setObjectFilter] = useState("");
  const [unitTypeFilter, setUnitTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState<"objektnummer" | "einheitsnummer" | "einheitstyp">("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const normalizeDebugText = (value: unknown) => String(value ?? "").trim();
  const objectFilterOptions = useMemo(
    () =>
      Array.from(new Set(units.map((unit) => normalizeDebugText(unit.objektnummer)).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "de", { numeric: true })
      ),
    [units]
  );
  const unitTypeFilterOptions = useMemo(
    () =>
      Array.from(new Set(units.map((unit) => normalizeDebugText(unit.einheitstyp)).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "de", { numeric: true })
      ),
    [units]
  );
  const filteredUnits = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = units.filter((unit) => {
      const objectOk = !objectFilter || normalizeDebugText(unit.objektnummer) === objectFilter;
      const typeOk = !unitTypeFilter || normalizeDebugText(unit.einheitstyp) === unitTypeFilter;
      const searchOk =
        !needle ||
        [unit.objektnummer, unit.einheitsnummer, unit.einheitstyp, unit.bezeichnung]
          .map((value) => normalizeDebugText(value))
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle);
      return objectOk && typeOk && searchOk;
    });
    const cmp = (a: unknown, b: unknown) =>
      normalizeDebugText(a).localeCompare(normalizeDebugText(b), "de", { numeric: true, sensitivity: "base" });
    filtered.sort((left, right) => {
      let result = 0;
      if (sortBy === "einheitsnummer") result = cmp(left.einheitsnummer, right.einheitsnummer);
      else if (sortBy === "einheitstyp") result = cmp(left.einheitstyp, right.einheitstyp);
      else result = cmp(left.objektnummer, right.objektnummer);
      if (result === 0) result = cmp(left.einheitsnummer, right.einheitsnummer);
      return sortDir === "desc" ? -result : result;
    });
    return filtered;
  }, [objectFilter, search, sortBy, sortDir, unitTypeFilter, units]);
  const toggleSort = (field: "objektnummer" | "einheitsnummer" | "einheitstyp") => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };
  const exportData = useMemo(() => {
    const headers = ["Objekt-Nr", "Einheit-Nr", "Art der Einheit", "Lage / Bezeichnung", "Hinweis"];
    const rows = filteredUnits.map((unit) => [
      unit.objektnummer,
      /^\d+$/.test(String(unit.einheitsnummer ?? "").trim()) ? String(unit.einheitsnummer).padStart(4, "0") : unit.einheitsnummer || "-",
      unit.einheitstyp || "-",
      unit.bezeichnung || "-",
      hint(unit)
    ]);
    const timestamp = new Date().toISOString().slice(0, 10);
    return {
      headers,
      rows,
      csvFilename: `${csvFilenamePrefix}-${timestamp}.csv`
    };
  }, [csvFilenamePrefix, filteredUnits, hint]);

  return (
    <section id={id} style={{ marginTop: 16, scrollMarginTop: 24 }}>
      <h3 style={{ margin: "0 0 8px" }}>{title}</h3>
      <section className="filterbar" style={{ marginTop: 10 }}>
        <input
          placeholder="Suche (Objekt, Einheit, Art, Lage)"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={objectFilter} onChange={(event) => setObjectFilter(event.target.value)}>
          <option value="">Alle Objekte</option>
          {objectFilterOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select value={unitTypeFilter} onChange={(event) => setUnitTypeFilter(event.target.value)}>
          <option value="">Alle Arten</option>
          {unitTypeFilterOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => downloadCsv(exportData.csvFilename, exportData.headers, exportData.rows)}
          disabled={exportData.rows.length === 0}
        >
          CSV Export
        </button>
      </section>
      <div style={{ marginTop: 8, fontSize: 12, color: "#5a6875" }}>
        Zeilen: {filteredUnits.length} / Gesamt relevant: {units.length}
        {ignoredInfoCount ? ` | Sonstiges/Info nicht kritisch: ${ignoredInfoCount}` : ""}
      </div>
      <div style={{ marginTop: 12, border: "1px solid var(--line)", borderRadius: 6, maxHeight: 360, overflow: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort("objektnummer")} style={{ border: "none", background: "transparent", font: "inherit", cursor: "pointer", fontWeight: 700, width: "100%", textAlign: "center" }}>
                  Objekt-Nr. {sortBy === "objektnummer" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("einheitsnummer")} style={{ border: "none", background: "transparent", font: "inherit", cursor: "pointer", fontWeight: 700, width: "100%", textAlign: "center" }}>
                  Einheit-Nr. {sortBy === "einheitsnummer" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("einheitstyp")} style={{ border: "none", background: "transparent", font: "inherit", cursor: "pointer", fontWeight: 700, width: "100%", textAlign: "center" }}>
                  Art der Einheit {sortBy === "einheitstyp" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                </button>
              </th>
              <th>Lage / Bezeichnung</th>
              <th>Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {filteredUnits.length ? (
              filteredUnits.map((unit) => (
                <tr key={unit.id}>
                  <td style={{ textAlign: "center" }}>
                    <Link className="badge object-number" to={`/objects?search=${encodeURIComponent(unit.objektnummer)}`} title={`Objekt ${unit.objektnummer}`}>
                      {unit.objektnummer}
                    </Link>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {/^\d+$/.test(String(unit.einheitsnummer ?? "").trim()) ? String(unit.einheitsnummer).padStart(4, "0") : unit.einheitsnummer || "-"}
                  </td>
                  <td style={{ textAlign: "center" }}>{unit.einheitstyp || "-"}</td>
                  <td>{unit.bezeichnung || "-"}</td>
                  <td>{hint(unit) || "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

type FocusUnitContractProbeRow = {
  objektnummer: string;
  einheitsnummer: string;
  apiUnitId: string;
  contractId: string;
  partyId: string;
  eigentuemer: string;
  type: string;
  isArchived: string;
  isCurrent: string;
  gueltigAb: string;
  gueltigBis: string;
  activeByWindow: string;
  activeByLegacy: string;
  status: string;
  hinweis: string;
};

type FocusObjectContractMatrixRow = {
  objektnummer: string;
  apiUnitId: string;
  einheitsnummer: string;
  aufgangRef: string;
  aufgangName: string;
  status: string;
  einheitstyp: string;
  bezeichnung: string;
  contractRows: number;
  ownerType3Rows: number;
  activeOwnerRows: number;
  earliestFrom: string;
  latestTo: string;
  fetchStatus: string;
  fetchWarning: string;
};

type FocusUnitContractTypeSweepProbeRow = {
  objektnummer: string;
  einheitsnummer: string;
  apiUnitId: string;
  typeFilter: string;
  queryKey: string;
  endpoint: string;
  status: string;
  rawRows: string;
  mappedRows: string;
  ownerType3Rows: string;
  activeOwnerRows: string;
  earliestOwnerFrom: string;
  latestOwnerTo: string;
  isLatestOwnerToPast: string;
  hasNext: string;
  sampleContractIds: string;
  hint: string;
};

type FocusUnitOwnershipProbeRow = {
  objektnummer: string;
  einheitsnummer: string;
  apiUnitId: string;
  propertyId: string;
  endpoint: string;
  status: string;
  payloadRows: string;
  embeddedRows: string;
  sourceRows: string;
  matchedRows: string;
  matchedKeySample: string;
  matchedSample: string;
  hint: string;
};

type FocusOwnerUnitProbeRow = {
  partyId: string;
  eigentuemer: string;
  objektnummer: string;
  einheitsnummer: string;
  apiUnitId: string;
  propertyId: string;
  contractId: string;
  type: string;
  isArchived: string;
  isCurrent: string;
  gueltigAb: string;
  gueltigBis: string;
  activeByWindow: string;
  activeByLegacy: string;
  status: string;
  hinweis: string;
};

type AssignmentAnomalyMultiActiveRow = {
  objektnummer: string;
  einheitsnummer: string;
  activeOwnersSummary: string;
};

type ObjectUnitConsistencyRow = {
  objektnummer: string;
  einheitsnummer: string;
};

const toRecordList = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object" && !Array.isArray(entry))
    : [];

const escapeCsv = (value: unknown) => {
  const text = String(value ?? "");
  if (/[;"\n\r]/.test(text)) return `"${text.replace(/"/g, "\"\"")}"`;
  return text;
};

const downloadCsv = (filename: string, headers: string[], rows: Array<Array<unknown>>) => {
  const csv = [headers.map(escapeCsv).join(";"), ...rows.map((row) => row.map(escapeCsv).join(";"))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const buildMissingOwnerContactList = (value: unknown): MissingOwnerContactListRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];

  const grouped = new Map<
    string,
    {
      partyId: string;
      objectNumbers: Set<string>;
      unitNumbers: Set<string>;
      ownerNames: Set<string>;
      contractIds: Set<string>;
      partyContactStatus: string;
      accountResolutionStatus: string;
    }
  >();

  for (const row of source) {
    const partyId = toText(row.ownerPartyId) || toText(row.partyId) || "(ohne Party-ID)";
    const key = partyId;
    const current =
      grouped.get(key) ??
      (() => {
        const created = {
          partyId,
          objectNumbers: new Set<string>(),
          unitNumbers: new Set<string>(),
          ownerNames: new Set<string>(),
          contractIds: new Set<string>(),
          partyContactStatus: toText(row.partyContactStatus) || "-",
          accountResolutionStatus: toText(row.accountResolutionStatus) || "-"
        };
        grouped.set(key, created);
        return created;
      })();

    const objectNumber = toText(row.objektnummer);
    const unitNumberRaw = toText(row.einheitsnummer) || toText(row.unitNumber);
    const unitIdRaw = toText(row.unitId);
    const unitNumber = unitNumberRaw || (unitIdRaw ? `ID:${unitIdRaw}` : "");
    const ownerName =
      toText(row.eigentuemername) ||
      toText(row.ownerName) ||
      toText(row.name1) ||
      toText(row.name_1) ||
      toText(row.fullName) ||
      toText(row.primaryName);
    const contractId = toText(row.ownerContractId);
    if (objectNumber) current.objectNumbers.add(objectNumber);
    if (unitNumber) current.unitNumbers.add(unitNumber);
    if (ownerName) current.ownerNames.add(ownerName);
    if (contractId) current.contractIds.add(contractId);
  }

  return Array.from(grouped.values())
    .map((entry) => ({
      partyId: entry.partyId,
      objectNumbers: Array.from(entry.objectNumbers),
      unitNumbers: Array.from(entry.unitNumbers),
      ownerNames: Array.from(entry.ownerNames),
      contractIds: Array.from(entry.contractIds),
      partyContactStatus: entry.partyContactStatus,
      accountResolutionStatus: entry.accountResolutionStatus
    }))
    .sort((a, b) => a.partyId.localeCompare(b.partyId, "de-DE"));
};

const buildAssignmentAnomalyNoActiveList = (value: unknown): AssignmentAnomalyNoActiveRow[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toText(entry))
    .filter(Boolean)
    .map((entry) => {
      const [obj, unit] = entry.split("/");
      return {
        objektnummer: toText(obj) || "-",
        einheitsnummer: toText(unit) || "-"
      };
    })
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}`.localeCompare(`${b.objektnummer}/${b.einheitsnummer}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const buildAssignmentAnomalyNoActiveReasonList = (value: unknown): AssignmentAnomalyNoActiveReasonRow[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toText(entry))
    .filter(Boolean)
    .map((entry) => {
      const [reason, countRaw] = entry.split("=");
      return {
        reason: toText(reason) || "-",
        count: Number(countRaw) || 0
      };
    })
    .sort((a, b) => b.count - a.count);
};

const buildAssignmentAnomalyNoActiveDetailList = (value: unknown): AssignmentAnomalyNoActiveDetailRow[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toText(entry))
    .filter(Boolean)
    .map((entry) => {
      const [left, right] = entry.split("->");
      const leftTrim = toText(left);
      const rightTrim = toText(right);
      const [obj, unit] = leftTrim.split("/");
      return {
        objektnummer: toText(obj) || "-",
        einheitsnummer: toText(unit) || "-",
        details: rightTrim || "-"
      };
    })
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}`.localeCompare(`${b.objektnummer}/${b.einheitsnummer}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const buildAssignmentAnomalyNoActiveContractProbeList = (
  value: unknown
): AssignmentAnomalyNoActiveContractProbeRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      eigentuemerId: toText(row.eigentuemerId) || "-",
      eigentuemername: toText(row.eigentuemername) || "-",
      assignmentId: toText(row.assignmentId) || "-",
      gueltigAb: toText(row.gueltigAb) || "-",
      gueltigBis: toText(row.gueltigBis) || "-",
      status: toText(row.status) || "-",
      reason: toText(row.reason) || "-"
    }))
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}/${a.assignmentId}`.localeCompare(
        `${b.objektnummer}/${b.einheitsnummer}/${b.assignmentId}`,
        "de-DE",
        {
          numeric: true,
          sensitivity: "base"
        }
      )
    );
};

const buildFocusUnitContractProbeList = (value: unknown): FocusUnitContractProbeRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      apiUnitId: toText(row.apiUnitId) || "-",
      contractId: toText(row.contractId) || "-",
      partyId: toText(row.partyId) || "-",
      eigentuemer: toText(row.eigentuemer) || "-",
      type: toText(row.type) || "-",
      isArchived: toText(row.isArchived) || "-",
      isCurrent: toText(row.isCurrent) || "-",
      gueltigAb: toText(row.gueltigAb) || "-",
      gueltigBis: toText(row.gueltigBis) || "-",
      activeByWindow: toText(row.activeByWindow) || "-",
      activeByLegacy: toText(row.activeByLegacy) || "-",
      status: toText(row.status) || "-",
      hinweis: toText(row.hinweis) || "-"
    }))
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}/${a.apiUnitId}/${a.contractId}`.localeCompare(
        `${b.objektnummer}/${b.einheitsnummer}/${b.apiUnitId}/${b.contractId}`,
        "de-DE",
        { numeric: true, sensitivity: "base" }
      )
    );
};

const buildFocusObjectContractMatrixList = (value: unknown): FocusObjectContractMatrixRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      apiUnitId: toText(row.apiUnitId) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      aufgangRef: toText(row.aufgangRef) || "-",
      aufgangName: toText(row.aufgangName) || "-",
      status: toText(row.status) || "-",
      einheitstyp: toText(row.einheitstyp) || "-",
      bezeichnung: toText(row.bezeichnung) || "-",
      contractRows: toNumber(row.contractRows) ?? 0,
      ownerType3Rows: toNumber(row.ownerType3Rows) ?? 0,
      activeOwnerRows: toNumber(row.activeOwnerRows) ?? 0,
      earliestFrom: toText(row.earliestFrom) || "-",
      latestTo: toText(row.latestTo) || "-",
      fetchStatus: toText(row.fetchStatus) || "-",
      fetchWarning: toText(row.fetchWarning) || "-"
    }))
    .sort((a, b) =>
      `${a.einheitsnummer}/${a.aufgangRef}/${a.apiUnitId}`.localeCompare(
        `${b.einheitsnummer}/${b.aufgangRef}/${b.apiUnitId}`,
        "de-DE",
        { numeric: true, sensitivity: "base" }
      )
    );
};

const buildFocusUnitContractTypeSweepProbeList = (value: unknown): FocusUnitContractTypeSweepProbeRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      apiUnitId: toText(row.apiUnitId) || "-",
      typeFilter: toText(row.typeFilter) || "-",
      queryKey: toText(row.queryKey) || "-",
      endpoint: toText(row.endpoint) || "-",
      status: toText(row.status) || "-",
      rawRows: toText(row.rawRows) || "-",
      mappedRows: toText(row.mappedRows) || "-",
      ownerType3Rows: toText(row.ownerType3Rows) || "-",
      activeOwnerRows: toText(row.activeOwnerRows) || "-",
      earliestOwnerFrom: toText(row.earliestOwnerFrom) || "-",
      latestOwnerTo: toText(row.latestOwnerTo) || "-",
      isLatestOwnerToPast: toText(row.isLatestOwnerToPast) || "-",
      hasNext: toText(row.hasNext) || "-",
      sampleContractIds: toText(row.sampleContractIds) || "-",
      hint: toText(row.hint) || "-"
    }))
    .sort((a, b) =>
      `${a.typeFilter}/${a.queryKey}/${a.apiUnitId}`.localeCompare(`${b.typeFilter}/${b.queryKey}/${b.apiUnitId}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const buildFocusUnitOwnershipProbeList = (value: unknown): FocusUnitOwnershipProbeRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      apiUnitId: toText(row.apiUnitId) || "-",
      propertyId: toText(row.propertyId) || "-",
      endpoint: toText(row.endpoint) || "-",
      status: toText(row.status) || "-",
      payloadRows: toText(row.payloadRows) || "-",
      embeddedRows: toText(row.embeddedRows) || "-",
      sourceRows: toText(row.sourceRows) || "-",
      matchedRows: toText(row.matchedRows) || "-",
      matchedKeySample: toText(row.matchedKeySample) || "-",
      matchedSample: toText(row.matchedSample) || "-",
      hint: toText(row.hint) || "-"
    }))
    .sort((a, b) =>
      `${a.propertyId}/${a.apiUnitId}/${a.endpoint}`.localeCompare(`${b.propertyId}/${b.apiUnitId}/${b.endpoint}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const buildFocusOwnerUnitProbeList = (value: unknown): FocusOwnerUnitProbeRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      partyId: toText(row.partyId) || "-",
      eigentuemer: toText(row.eigentuemer) || "-",
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-",
      apiUnitId: toText(row.apiUnitId) || "-",
      propertyId: toText(row.propertyId) || "-",
      contractId: toText(row.contractId) || "-",
      type: toText(row.type) || "-",
      isArchived: toText(row.isArchived) || "-",
      isCurrent: toText(row.isCurrent) || "-",
      gueltigAb: toText(row.gueltigAb) || "-",
      gueltigBis: toText(row.gueltigBis) || "-",
      activeByWindow: toText(row.activeByWindow) || "-",
      activeByLegacy: toText(row.activeByLegacy) || "-",
      status: toText(row.status) || "-",
      hinweis: toText(row.hinweis) || "-"
    }))
    .sort((a, b) =>
      `${a.status}/${a.objektnummer}/${a.einheitsnummer}/${a.contractId}`.localeCompare(
        `${b.status}/${b.objektnummer}/${b.einheitsnummer}/${b.contractId}`,
        "de-DE",
        {
          numeric: true,
          sensitivity: "base"
        }
      )
    );
};

const buildAssignmentAnomalyMultiActiveList = (value: unknown): AssignmentAnomalyMultiActiveRow[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toText(entry))
    .filter(Boolean)
    .map((entry) => {
      const [left, right] = entry.split("->");
      const leftTrim = toText(left);
      const rightTrim = toText(right);
      const [obj, unit] = leftTrim.split("/");
      return {
        objektnummer: toText(obj) || "-",
        einheitsnummer: toText(unit) || "-",
        activeOwnersSummary: rightTrim || "-"
      };
    })
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}`.localeCompare(`${b.objektnummer}/${b.einheitsnummer}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const buildObjectUnitConsistencyList = (value: unknown): ObjectUnitConsistencyRow[] => {
  const source = toRecordList(value);
  if (!source.length) return [];
  return source
    .map((row) => ({
      objektnummer: toText(row.objektnummer) || "-",
      einheitsnummer: toText(row.einheitsnummer) || "-"
    }))
    .sort((a, b) =>
      `${a.objektnummer}/${a.einheitsnummer}`.localeCompare(`${b.objektnummer}/${b.einheitsnummer}`, "de-DE", {
        numeric: true,
        sensitivity: "base"
      })
    );
};

const debugTitleIcon = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M13 2 5 13h6l-1 9 9-13h-6l1-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

const renderFaciliooDebug = (row: AuditRow) => {
  const changes = toRecord(row.changes_json);
  const syncErrorMessage = toText(changes?.error);
  const debug = toRecord(changes?.debug);
  if (!debug) {
    if (!syncErrorMessage) return <span style={{ opacity: 0.7 }}>-</span>;
    return (
      <div style={{ color: "#b42318", fontWeight: 600 }}>
        <strong>Abbruchgrund:</strong> {syncErrorMessage}
      </div>
    );
  }

  const owners = toRecord(debug.owners);
  const ownerProbes = toRecord(owners?.probes);
  const missingOwnerContactList = buildMissingOwnerContactList(ownerProbes?.noContactSamples);
  const assignmentAnomalyNoActiveList = buildAssignmentAnomalyNoActiveList(owners?.assignmentAnomalyNoActiveProbe);
  const assignmentAnomalyNoActiveReasonList = buildAssignmentAnomalyNoActiveReasonList(owners?.assignmentAnomalyNoActiveReasonSummary);
  const assignmentAnomalyNoActiveDetailList = buildAssignmentAnomalyNoActiveDetailList(owners?.assignmentAnomalyNoActiveDetailProbe);
  const assignmentAnomalyNoActiveContractProbeList = buildAssignmentAnomalyNoActiveContractProbeList(
    owners?.assignmentAnomalyNoActiveContractProbe
  );
  const focusUnitContractProbeList = buildFocusUnitContractProbeList(owners?.focusUnitContractProbe);
  const focusUnitContractTypeSweepProbeList = buildFocusUnitContractTypeSweepProbeList(
    owners?.focusUnitContractTypeSweepProbe
  );
  const focusUnitOwnershipProbeList = buildFocusUnitOwnershipProbeList(owners?.focusUnitOwnershipProbe);
  const focusOwnerUnitProbeList = buildFocusOwnerUnitProbeList(owners?.focusOwnerUnitProbe);
  const focusObjectContractMatrixList = buildFocusObjectContractMatrixList(owners?.focusObjectContractMatrixProbe);
  const focusUnitProbeConfig = toRecord(owners?.focusUnitProbeConfig);
  const focusOwnerUnitProbeConfig = toRecord(owners?.focusOwnerUnitProbeConfig);
  const assignmentAnomalyMultiActiveList = buildAssignmentAnomalyMultiActiveList(owners?.assignmentAnomalyMultiActiveProbe);
  const units = toRecord(debug.units);
  const unitConsistency = toRecord(units?.consistency);
  const objectUnitConsistencyList = buildObjectUnitConsistencyList(unitConsistency?.objectUnitConsistencyProbe);
  const unitSkips = toRecord(debug.unitSkips);
  const progress = toRecord(debug.progress);
  const errorMeta = toRecord(debug.error);
  const errorReason = toText(errorMeta?.reason);
  const errorTarget = toText(errorMeta?.target);
  const errorTimeoutMs = toNumber(errorMeta?.timeoutMs);
  const errorAttempt = toNumber(errorMeta?.attempt);
  const errorMaxAttempts = toNumber(errorMeta?.maxAttempts);
  const errorOriginalMessage = toText(errorMeta?.originalMessage);

  const unresolved = toNumber(owners?.unresolvedAccountId);
  const noContacts = toNumber(owners?.withoutUsableContacts);
  const accountViaMap = toNumber(owners?.accountResolvedViaMap);
  const accountViaParty = toNumber(owners?.accountResolvedViaParty);
  const accountDirect = toNumber(owners?.accountResolvedDirect);
  const unitWarn = toText(units?.warning);
  const unitDiag = toText(units?.diagnostics);
  const skipNoObject = toNumber(unitSkips?.noObjectNumber);
  const skipNoUnit = toNumber(unitSkips?.noUnitNumber);
  const phase = toText(debug.phase);
  const progressMsg = toText(progress?.message);
  const trigger = toText(debug.trigger);
  const durationMs = toNumber(debug.durationMs);
  const mirrorCleanup = toRecord(debug.mirrorCleanup);
  const inApp = toRecord(debug.inApp);
  const inAppObjects = toRecord(inApp?.objects);
  const inAppUnits = toRecord(inApp?.units);
  const contactDebug = toText(debug.contactDebug);
  const debugSectionStyle: React.CSSProperties = {
    marginTop: 14,
    paddingTop: 10,
    borderTop: "1px solid var(--line)"
  };

  return (
    <details style={{ width: "100%", minWidth: 0 }}>
      <summary style={{ cursor: "pointer", fontWeight: 600, display: "block", width: "100%" }}>
        {`Eigentümer ohne Account: ${unresolved ?? 0}, ohne Kontaktdaten: ${noContacts ?? 0}`}
      </summary>
      <div style={{ display: "grid", gap: 4, marginTop: 8, width: "100%" }}>
        <div>
          <strong>Trigger:</strong> {trigger || "-"}
          {durationMs !== null ? ` | Dauer: ${Math.round(durationMs / 1000)} s` : ""}
        </div>
        <div>
          <strong>Eigentümer:</strong>{" "}
          {`ohneAccount=${unresolved ?? 0}, ohneKontaktdaten=${noContacts ?? 0}, direkt=${accountDirect ?? 0}, viaParty=${accountViaParty ?? 0}, viaMap=${accountViaMap ?? 0}`}
        </div>
        <div>
          <strong>Einheiten-Skips:</strong> {`ohneObjektNr=${skipNoObject ?? 0}, ohneEinheitsNr=${skipNoUnit ?? 0}`}
        </div>
        <div>
          <strong>Sync-Phase:</strong> {phase || "-"} {progressMsg ? `| ${progressMsg}` : ""}
        </div>
        {syncErrorMessage ? (
          <div style={{ color: "#b42318", fontWeight: 600 }}>
            <strong>Abbruchgrund:</strong> {syncErrorMessage}
            {errorReason ? ` | Typ: ${errorReason}` : ""}
            {errorTimeoutMs !== null ? ` | Timeout: ${Math.round(errorTimeoutMs / 1000)}s` : ""}
            {errorAttempt !== null && errorMaxAttempts !== null ? ` | Versuch: ${errorAttempt}/${errorMaxAttempts}` : ""}
            {errorTarget ? ` | Endpoint: ${errorTarget}` : ""}
            {errorOriginalMessage ? ` | Original: ${errorOriginalMessage}` : ""}
          </div>
        ) : null}
        <div>
          <strong>Units-Warnung:</strong> {unitWarn || "-"}
        </div>
        <div>
          <strong>Units-Diagnose:</strong> {unitDiag || "-"}
        </div>
        <div>
          <strong>Objekt/Einheit-Konsistenz:</strong>{" "}
          {`inaktiveObjekte=${toNumber(unitConsistency?.inactiveObjectsTotal) ?? 0}, aktivInInaktivemObjekt(vorKaskade)=${toNumber(unitConsistency?.activeUnitsInInactiveObjectsBefore) ?? 0}, perKaskadeInaktiv=${toNumber(unitConsistency?.cascadeToInactive) ?? 0}`}
        </div>
        <div>
          <strong>Probes:</strong>{" "}
          {`accountIds=${toListText(ownerProbes?.accountIdProbe)}, noAccount=${toListText(ownerProbes?.noAccountWarnings)}, contactFehler=${toListText(
            ownerProbes?.accountContactWarnings
          )}`}
        </div>
        {missingOwnerContactList.length ? (
          <div style={debugSectionStyle}>
            <strong>Fehlende Eigentümer-Kontaktdaten (separate Liste):</strong>{" "}
            {`${missingOwnerContactList.length} eindeutige Party-ID(s)`}
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 860 }}>
                <thead>
                  <tr>
                    <th>Party-ID</th>
                    <th>Eigentümername</th>
                    <th>Objekt(e)</th>
                    <th>Einheit(en)</th>
                    <th>Contract-ID(s)</th>
                    <th>Party-Kontakt</th>
                    <th>Account-Auflösung</th>
                  </tr>
                </thead>
                <tbody>
                  {missingOwnerContactList.map((entry) => (
                    <tr key={entry.partyId}>
                      <td>{entry.partyId}</td>
                      <td>{entry.ownerNames.length ? entry.ownerNames.join(" | ") : "-"}</td>
                      <td>{entry.objectNumbers.length ? entry.objectNumbers.join(", ") : "-"}</td>
                      <td>{entry.unitNumbers.length ? entry.unitNumbers.join(", ") : "-"}</td>
                      <td>{entry.contractIds.length ? entry.contractIds.join(", ") : "-"}</td>
                      <td>{entry.partyContactStatus || "-"}</td>
                      <td>{entry.accountResolutionStatus || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {assignmentAnomalyNoActiveList.length ? (
          <div style={debugSectionStyle}>
            <strong>Zuordnungs-Anomalien: Einheiten ohne aktive Zuordnung (separate Liste):</strong>{" "}
            {`${assignmentAnomalyNoActiveList.length} Einheiten`}
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-zuordnungsanomalie-ohne-aktiv-${row.id}.csv`,
                    ["Objekt-Nr", "Einheit-Nr"],
                    assignmentAnomalyNoActiveList.map((entry) => [entry.objektnummer, entry.einheitsnummer])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 360 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentAnomalyNoActiveList.map((entry) => (
                    <tr key={`${entry.objektnummer}/${entry.einheitsnummer}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {assignmentAnomalyNoActiveReasonList.length ? (
          <div style={debugSectionStyle}>
            <strong>Zuordnungs-Anomalien ohne aktive Zuordnung: Gründe</strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-zuordnungsanomalie-ohne-aktiv-gruende-${row.id}.csv`,
                    ["Grund", "Anzahl"],
                    assignmentAnomalyNoActiveReasonList.map((entry) => [entry.reason, entry.count])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 360 }}>
                <thead>
                  <tr>
                    <th>Grund</th>
                    <th>Anzahl</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentAnomalyNoActiveReasonList.map((entry) => (
                    <tr key={entry.reason}>
                      <td>{entry.reason}</td>
                      <td>{entry.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {assignmentAnomalyNoActiveDetailList.length ? (
          <div style={debugSectionStyle}>
            <strong>Zuordnungs-Anomalien ohne aktive Zuordnung: Detailprobe</strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-zuordnungsanomalie-ohne-aktiv-detail-${row.id}.csv`,
                    ["Objekt-Nr", "Einheit-Nr", "Details"],
                    assignmentAnomalyNoActiveDetailList.map((entry) => [entry.objektnummer, entry.einheitsnummer, entry.details])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 980 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentAnomalyNoActiveDetailList.map((entry) => (
                    <tr key={`${entry.objektnummer}/${entry.einheitsnummer}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {assignmentAnomalyNoActiveContractProbeList.length ? (
          <div style={debugSectionStyle}>
            <strong>Zuordnungs-Anomalien ohne aktive Zuordnung: Vertragskandidaten</strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-zuordnungsanomalie-ohne-aktiv-vertragskandidaten-${row.id}.csv`,
                    [
                      "Objekt-Nr",
                      "Einheit-Nr",
                      "Eigentümer-ID",
                      "Eigentümername",
                      "Zuordnung-ID",
                      "Gültig ab",
                      "Gültig bis",
                      "Status",
                      "Grund"
                    ],
                    assignmentAnomalyNoActiveContractProbeList.map((entry) => [
                      entry.objektnummer,
                      entry.einheitsnummer,
                      entry.eigentuemerId,
                      entry.eigentuemername,
                      entry.assignmentId,
                      entry.gueltigAb,
                      entry.gueltigBis,
                      entry.status,
                      entry.reason
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1120 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>Eigentümer-ID</th>
                    <th>Eigentümer</th>
                    <th>Zuordnung-ID</th>
                    <th>Gültig ab</th>
                    <th>Gültig bis</th>
                    <th>Status</th>
                    <th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentAnomalyNoActiveContractProbeList.map((entry, index) => (
                    <tr
                      key={`${entry.objektnummer}/${entry.einheitsnummer}/${entry.assignmentId}/${entry.eigentuemerId}/${index}`}
                    >
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.eigentuemerId}</td>
                      <td>{entry.eigentuemername}</td>
                      <td>{entry.assignmentId}</td>
                      <td>{entry.gueltigAb}</td>
                      <td>{entry.gueltigBis}</td>
                      <td>{entry.status}</td>
                      <td>{entry.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {focusUnitContractTypeSweepProbeList.length ? (
          <div style={debugSectionStyle}>
            <strong>
              Fokus-Einheit Contract-Type-Sweep (
              {`${toText(focusUnitProbeConfig?.objektnummer) || "0013"}/${toText(focusUnitProbeConfig?.einheitsnummer) || "2"}`})
            </strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-fokus-einheit-type-sweep-${row.id}.csv`,
                    [
                      "Objekt-Nr",
                      "Einheit-Nr",
                      "API Unit-ID",
                      "Type-Filter",
                      "Query-Key",
                      "Endpoint",
                      "Status",
                      "Raw Rows",
                      "Mapped Rows",
                      "Owner Typ 3",
                      "Active Owner",
                      "Earliest Owner From",
                      "Latest Owner To",
                      "Latest Owner To in Vergangenheit",
                      "hasNext",
                      "Sample Contract IDs",
                      "Hinweis"
                    ],
                    focusUnitContractTypeSweepProbeList.map((entry) => [
                      entry.objektnummer,
                      entry.einheitsnummer,
                      entry.apiUnitId,
                      entry.typeFilter,
                      entry.queryKey,
                      entry.endpoint,
                      entry.status,
                      entry.rawRows,
                      entry.mappedRows,
                      entry.ownerType3Rows,
                      entry.activeOwnerRows,
                      entry.earliestOwnerFrom,
                      entry.latestOwnerTo,
                      entry.isLatestOwnerToPast,
                      entry.hasNext,
                      entry.sampleContractIds,
                      entry.hint
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1750 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>API Unit-ID</th>
                    <th>Type-Filter</th>
                    <th>Query-Key</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Raw</th>
                    <th>Mapped</th>
                    <th>Owner Typ 3</th>
                    <th>Active Owner</th>
                    <th>Earliest</th>
                    <th>Latest</th>
                    <th>Latest Past</th>
                    <th>hasNext</th>
                    <th>Sample Contract IDs</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {focusUnitContractTypeSweepProbeList.map((entry, index) => (
                    <tr key={`${entry.apiUnitId}/${entry.typeFilter}/${entry.queryKey}/${index}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.apiUnitId}</td>
                      <td>{entry.typeFilter}</td>
                      <td>{entry.queryKey}</td>
                      <td>{entry.endpoint}</td>
                      <td>{entry.status}</td>
                      <td>{entry.rawRows}</td>
                      <td>{entry.mappedRows}</td>
                      <td>{entry.ownerType3Rows}</td>
                      <td>{entry.activeOwnerRows}</td>
                      <td>{entry.earliestOwnerFrom}</td>
                      <td>{entry.latestOwnerTo}</td>
                      <td>{entry.isLatestOwnerToPast}</td>
                      <td>{entry.hasNext}</td>
                      <td>{entry.sampleContractIds}</td>
                      <td>{entry.hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {focusUnitOwnershipProbeList.length ? (
          <div style={debugSectionStyle}>
            <strong>
              Fokus-Einheit Ownership-Endpoints (
              {`${toText(focusUnitProbeConfig?.objektnummer) || "0013"}/${toText(focusUnitProbeConfig?.einheitsnummer) || "2"}`})
            </strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-fokus-einheit-ownership-endpoints-${row.id}.csv`,
                    [
                      "Objekt-Nr",
                      "Einheit-Nr",
                      "API Unit-ID",
                      "Property-ID",
                      "Endpoint",
                      "Status",
                      "Payload Rows",
                      "Embedded Rows",
                      "Source Rows",
                      "Matched Rows",
                      "Matched Key Sample",
                      "Matched Sample",
                      "Hinweis"
                    ],
                    focusUnitOwnershipProbeList.map((entry) => [
                      entry.objektnummer,
                      entry.einheitsnummer,
                      entry.apiUnitId,
                      entry.propertyId,
                      entry.endpoint,
                      entry.status,
                      entry.payloadRows,
                      entry.embeddedRows,
                      entry.sourceRows,
                      entry.matchedRows,
                      entry.matchedKeySample,
                      entry.matchedSample,
                      entry.hint
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1750 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>API Unit-ID</th>
                    <th>Property-ID</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Payload</th>
                    <th>Embedded</th>
                    <th>Source</th>
                    <th>Matched</th>
                    <th>Matched Key Sample</th>
                    <th>Matched Sample</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {focusUnitOwnershipProbeList.map((entry, index) => (
                    <tr key={`${entry.propertyId}/${entry.apiUnitId}/${entry.endpoint}/${index}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.apiUnitId}</td>
                      <td>{entry.propertyId}</td>
                      <td>{entry.endpoint}</td>
                      <td>{entry.status}</td>
                      <td>{entry.payloadRows}</td>
                      <td>{entry.embeddedRows}</td>
                      <td>{entry.sourceRows}</td>
                      <td>{entry.matchedRows}</td>
                      <td>{entry.matchedKeySample}</td>
                      <td>{entry.matchedSample}</td>
                      <td>{entry.hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {focusOwnerUnitProbeList.length ? (
          <div style={debugSectionStyle}>
            <strong>
              Fokus-Eigentümer Units (
              {`${toText(focusOwnerUnitProbeConfig?.partyId) || "788388"}${
                toText(focusOwnerUnitProbeConfig?.objektnummer)
                  ? ` | Objekt ${toText(focusOwnerUnitProbeConfig?.objektnummer)}`
                  : ""
              }`})
            </strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-fokus-eigentuemer-units-${row.id}.csv`,
                    [
                      "Party-ID",
                      "Eigentümer",
                      "Objekt-Nr",
                      "Einheit-Nr",
                      "API Unit-ID",
                      "Property-ID",
                      "Contract-ID",
                      "Typ",
                      "isArchived",
                      "isCurrent",
                      "Gültig ab",
                      "Gültig bis",
                      "Aktiv (Fenster)",
                      "Aktiv (Legacy)",
                      "Status",
                      "Hinweis"
                    ],
                    focusOwnerUnitProbeList.map((entry) => [
                      entry.partyId,
                      entry.eigentuemer,
                      entry.objektnummer,
                      entry.einheitsnummer,
                      entry.apiUnitId,
                      entry.propertyId,
                      entry.contractId,
                      entry.type,
                      entry.isArchived,
                      entry.isCurrent,
                      entry.gueltigAb,
                      entry.gueltigBis,
                      entry.activeByWindow,
                      entry.activeByLegacy,
                      entry.status,
                      entry.hinweis
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1700 }}>
                <thead>
                  <tr>
                    <th>Party-ID</th>
                    <th>Eigentümer</th>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>API Unit-ID</th>
                    <th>Property-ID</th>
                    <th>Contract-ID</th>
                    <th>Typ</th>
                    <th>isArchived</th>
                    <th>isCurrent</th>
                    <th>Gültig ab</th>
                    <th>Gültig bis</th>
                    <th>Aktiv (Fenster)</th>
                    <th>Aktiv (Legacy)</th>
                    <th>Status</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {focusOwnerUnitProbeList.map((entry, index) => (
                    <tr
                      key={`${entry.status}/${entry.partyId}/${entry.objektnummer}/${entry.einheitsnummer}/${entry.contractId}/${index}`}
                    >
                      <td>{entry.partyId}</td>
                      <td>{entry.eigentuemer}</td>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.apiUnitId}</td>
                      <td>{entry.propertyId}</td>
                      <td>{entry.contractId}</td>
                      <td>{entry.type}</td>
                      <td>{entry.isArchived}</td>
                      <td>{entry.isCurrent}</td>
                      <td>{entry.gueltigAb}</td>
                      <td>{entry.gueltigBis}</td>
                      <td>{entry.activeByWindow}</td>
                      <td>{entry.activeByLegacy}</td>
                      <td>{entry.status}</td>
                      <td>{entry.hinweis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {focusUnitContractProbeList.length ? (
          <div style={debugSectionStyle}>
            <strong>
              Fokus-Einheit Vertragsprobe (
              {`${toText(focusUnitProbeConfig?.objektnummer) || "0013"}/${toText(focusUnitProbeConfig?.einheitsnummer) || "2"}`})
            </strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-fokus-einheit-vertragsprobe-${row.id}.csv`,
                    [
                      "Objekt-Nr",
                      "Einheit-Nr",
                      "API Unit-ID",
                      "Contract-ID",
                      "Party-ID",
                      "Eigentümer",
                      "Typ",
                      "isArchived",
                      "isCurrent",
                      "Gültig ab",
                      "Gültig bis",
                      "Aktiv (Fenster)",
                      "Aktiv (Legacy)",
                      "Status",
                      "Hinweis"
                    ],
                    focusUnitContractProbeList.map((entry) => [
                      entry.objektnummer,
                      entry.einheitsnummer,
                      entry.apiUnitId,
                      entry.contractId,
                      entry.partyId,
                      entry.eigentuemer,
                      entry.type,
                      entry.isArchived,
                      entry.isCurrent,
                      entry.gueltigAb,
                      entry.gueltigBis,
                      entry.activeByWindow,
                      entry.activeByLegacy,
                      entry.status,
                      entry.hinweis
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1480 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>API Unit-ID</th>
                    <th>Contract-ID</th>
                    <th>Party-ID</th>
                    <th>Eigentümer</th>
                    <th>Typ</th>
                    <th>isArchived</th>
                    <th>isCurrent</th>
                    <th>Gültig ab</th>
                    <th>Gültig bis</th>
                    <th>Aktiv (Fenster)</th>
                    <th>Aktiv (Legacy)</th>
                    <th>Status</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {focusUnitContractProbeList.map((entry, index) => (
                    <tr
                      key={`${entry.objektnummer}/${entry.einheitsnummer}/${entry.apiUnitId}/${entry.contractId}/${entry.partyId}/${index}`}
                    >
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.apiUnitId}</td>
                      <td>{entry.contractId}</td>
                      <td>{entry.partyId}</td>
                      <td>{entry.eigentuemer}</td>
                      <td>{entry.type}</td>
                      <td>{entry.isArchived}</td>
                      <td>{entry.isCurrent}</td>
                      <td>{entry.gueltigAb}</td>
                      <td>{entry.gueltigBis}</td>
                      <td>{entry.activeByWindow}</td>
                      <td>{entry.activeByLegacy}</td>
                      <td>{entry.status}</td>
                      <td>{entry.hinweis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {focusObjectContractMatrixList.length ? (
          <div style={debugSectionStyle}>
            <strong>
              Fokus-Objekt Unit-/Vertragsmatrix (
              {`${toText(focusUnitProbeConfig?.objektnummer) || "0013"}`})
            </strong>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-fokus-objekt-vertragsmatrix-${row.id}.csv`,
                    [
                      "Objekt-Nr",
                      "API Unit-ID",
                      "Einheit-Nr",
                      "Aufgang-Ref",
                      "Aufgang-Name",
                      "Status",
                      "Einheitstyp",
                      "Bezeichnung",
                      "Verträge gesamt",
                      "Owner-Verträge (Typ 3)",
                      "Aktive Owner-Verträge",
                      "Frühestes von",
                      "Spätestes bis",
                      "Fetch-Status",
                      "Fetch-Warnung"
                    ],
                    focusObjectContractMatrixList.map((entry) => [
                      entry.objektnummer,
                      entry.apiUnitId,
                      entry.einheitsnummer,
                      entry.aufgangRef,
                      entry.aufgangName,
                      entry.status,
                      entry.einheitstyp,
                      entry.bezeichnung,
                      entry.contractRows,
                      entry.ownerType3Rows,
                      entry.activeOwnerRows,
                      entry.earliestFrom,
                      entry.latestTo,
                      entry.fetchStatus,
                      entry.fetchWarning
                    ])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 1600 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>API Unit-ID</th>
                    <th>Einheit-Nr</th>
                    <th>Aufgang-Ref</th>
                    <th>Aufgang-Name</th>
                    <th>Status</th>
                    <th>Einheitstyp</th>
                    <th>Bezeichnung</th>
                    <th>Verträge gesamt</th>
                    <th>Owner-Verträge (Typ 3)</th>
                    <th>Aktive Owner-Verträge</th>
                    <th>Frühestes von</th>
                    <th>Spätestes bis</th>
                    <th>Fetch-Status</th>
                    <th>Fetch-Warnung</th>
                  </tr>
                </thead>
                <tbody>
                  {focusObjectContractMatrixList.map((entry, index) => (
                    <tr key={`${entry.apiUnitId}/${entry.einheitsnummer}/${entry.aufgangRef}/${index}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.apiUnitId}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.aufgangRef}</td>
                      <td>{entry.aufgangName}</td>
                      <td>{entry.status}</td>
                      <td>{entry.einheitstyp}</td>
                      <td>{entry.bezeichnung}</td>
                      <td>{entry.contractRows}</td>
                      <td>{entry.ownerType3Rows}</td>
                      <td>{entry.activeOwnerRows}</td>
                      <td>{entry.earliestFrom}</td>
                      <td>{entry.latestTo}</td>
                      <td>{entry.fetchStatus}</td>
                      <td>{entry.fetchWarning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {assignmentAnomalyMultiActiveList.length ? (
          <div style={debugSectionStyle}>
            <strong>Zuordnungs-Anomalien: Einheiten mit mehrfach aktiver Zuordnung (separate Liste):</strong>{" "}
            {`${assignmentAnomalyMultiActiveList.length} Einheiten`}
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    `debug-zuordnungsanomalie-mehrfach-aktiv-${row.id}.csv`,
                    ["Objekt-Nr", "Einheit-Nr", "Aktive Eigentümer"],
                    assignmentAnomalyMultiActiveList.map((entry) => [entry.objektnummer, entry.einheitsnummer, entry.activeOwnersSummary])
                  )
                }
              >
                CSV Export
              </button>
            </div>
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 980 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                    <th>Aktive Eigentümer</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentAnomalyMultiActiveList.map((entry) => (
                    <tr key={`${entry.objektnummer}/${entry.einheitsnummer}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                      <td>{entry.activeOwnersSummary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {objectUnitConsistencyList.length ? (
          <div style={debugSectionStyle}>
            <strong>Objekt/Einheit-Anomalien (separate Liste):</strong>{" "}
            {`${objectUnitConsistencyList.length} Einheiten (vor Kaskade aktiv in inaktivem Objekt)`}
            <div style={{ marginTop: 6, overflowX: "auto" }}>
              <table style={{ minWidth: 360 }}>
                <thead>
                  <tr>
                    <th>Objekt-Nr</th>
                    <th>Einheit-Nr</th>
                  </tr>
                </thead>
                <tbody>
                  {objectUnitConsistencyList.map((entry) => (
                    <tr key={`${entry.objektnummer}/${entry.einheitsnummer}`}>
                      <td>{entry.objektnummer}</td>
                      <td>{entry.einheitsnummer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        <div>
          <strong>Mirror-Cleanup:</strong>{" "}
          {`Kandidaten=${toNumber(mirrorCleanup?.candidates) ?? 0}, gelöscht=${toNumber(mirrorCleanup?.deleted) ?? 0}`}
        </div>
        <div>
          <strong>In App:</strong>{" "}
          {`Objekte ${toNumber(inAppObjects?.active) ?? 0}/${toNumber(inAppObjects?.total) ?? 0}, Einheiten ${toNumber(inAppUnits?.active) ?? 0}/${toNumber(inAppUnits?.total) ?? 0}`}
        </div>
        {contactDebug ? (
          <div>
            <strong>Kontakt-Debug:</strong> {contactDebug}
          </div>
        ) : null}
      </div>
    </details>
  );
};

const AuditPageContent = () => {
  const isFrameworkV2 =
    typeof document !== "undefined" && document.body.classList.contains("framework-v2");
  const [syncPage, setSyncPage] = useState(1);
  const [syncStatusFilter, setSyncStatusFilter] = useState<"all" | "ok" | "error">("all");
  const [syncTriggerFilter, setSyncTriggerFilter] = useState<"all" | "manual" | "auto">("all");
  const [faciliooDebugAccountId, setFaciliooDebugAccountId] = useState("");
  const [faciliooDebugContractId, setFaciliooDebugContractId] = useState("");
  const [faciliooDebugProcessRef, setFaciliooDebugProcessRef] = useState("");
  const [faciliooProcessFeedTestEnabled, setFaciliooProcessFeedTestEnabled] = useState(false);
  const [faciliooProcessFeedTestText, setFaciliooProcessFeedTestText] = useState("");
  const [faciliooMasterDataProbePending, setFaciliooMasterDataProbePending] = useState(false);
  const [faciliooMasterDataProbe, setFaciliooMasterDataProbe] = useState<FaciliooMasterDataProbeResult | null>(null);
  const [faciliooMasterDataProbeError, setFaciliooMasterDataProbeError] = useState<string | null>(null);
  const [faciliooContractTestPending, setFaciliooContractTestPending] = useState(false);
  const [faciliooContractTestResult, setFaciliooContractTestResult] = useState<FaciliooContractLiveResult | null>(null);
  const [faciliooContractTestError, setFaciliooContractTestError] = useState<string | null>(null);
  const [faciliooProcessTestPending, setFaciliooProcessTestPending] = useState(false);
  const [faciliooProcessTestResult, setFaciliooProcessTestResult] = useState<FaciliooProcessResolveResult | null>(null);
  const [faciliooProcessTestError, setFaciliooProcessTestError] = useState<string | null>(null);
  const [faciliooDebugStatus, setFaciliooDebugStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-audit"],
    queryFn: () => api.uiSettings()
  });
  const faciliooEnabled = uiSettings?.management?.faciliooEnabled !== false;
  const { data: faciliooSyncStatusData, error: faciliooSyncStatusError } = useQuery({
    queryKey: ["facilioo-sync-status-debug-page"],
    queryFn: () => api.getFaciliooSyncStatus(),
    enabled: faciliooEnabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false
  });
  const { data: assignmentAnomalies, error: assignmentAnomaliesError } = useQuery({
    queryKey: ["debug-assignment-anomalies"],
    queryFn: () => api.assignmentAnomalies(),
    retry: false,
    throwOnError: false
  });
  const unitsWithoutActiveAssignment = useMemo(
    () => assignmentAnomalies?.unitsWithoutActiveAssignment ?? [],
    [assignmentAnomalies?.unitsWithoutActiveAssignment]
  );
  const unitsWithMultipleActiveAssignments = useMemo(
    () => assignmentAnomalies?.unitsWithMultipleActiveAssignments ?? [],
    [assignmentAnomalies?.unitsWithMultipleActiveAssignments]
  );
  const lastRun = faciliooSyncStatusData?.lastRun;
  const lastRunLabel = lastRun?.finishedAt || lastRun?.startedAt ? new Date(lastRun.finishedAt || lastRun.startedAt || "").toLocaleString("de-DE") : "-";
  const datasetStats = [
    { key: "objects", label: "Objekte", stats: lastRun?.datasets?.objects },
    { key: "units", label: "Einheiten", stats: lastRun?.datasets?.units },
    { key: "owners", label: "Eigentümer", stats: lastRun?.datasets?.owners },
    { key: "assignments", label: "Zuordnungen", stats: lastRun?.datasets?.assignments }
  ] as const;

  const { data: faciliooSyncData } = useQuery({
    queryKey: ["facilioo-sync-audit-page", syncPage, syncStatusFilter, syncTriggerFilter],
    queryFn: () =>
      api.faciliooSyncLogs({
        page: syncPage,
        pageSize: 25,
        status: syncStatusFilter === "all" ? undefined : syncStatusFilter,
        trigger: syncTriggerFilter === "all" ? undefined : syncTriggerFilter
      }),
    enabled: faciliooEnabled
  });
  const syncRows = useMemo(
    () => (Array.isArray(faciliooSyncData?.data) ? faciliooSyncData.data.filter(isRecord) : []),
    [faciliooSyncData?.data]
  );
  const syncTotal = faciliooSyncData?.meta.total ?? 0;
  const syncPageSize = faciliooSyncData?.meta.pageSize ?? 25;
  const syncTotalPages = Math.max(1, Math.ceil(syncTotal / syncPageSize));

  const runFaciliooMasterDataProbe = async () => {
    setFaciliooMasterDataProbePending(true);
    setFaciliooMasterDataProbeError(null);
    try {
      const result = await api.probeFaciliooMasterData();
      setFaciliooMasterDataProbe(result);
    } catch (error) {
      setFaciliooMasterDataProbe(null);
      setFaciliooMasterDataProbeError(error instanceof Error ? error.message : "Master-Data-Probe fehlgeschlagen");
    } finally {
      setFaciliooMasterDataProbePending(false);
    }
  };

  const runFaciliooContactDebugSyncNow = async () => {
    const accountId = faciliooDebugAccountId.trim();
    if (!accountId) {
      setFaciliooDebugStatus({ type: "error", text: "Bitte eine Debug-Account-ID eingeben." });
      return;
    }
    try {
      const result = await api.runFaciliooSyncNow(undefined, accountId);
      setFaciliooDebugStatus({ type: "ok", text: result.message || "Kontakt-Debug-Sync gestartet." });
    } catch (error) {
      setFaciliooDebugStatus({ type: "error", text: error instanceof Error ? error.message : "Kontakt-Debug-Sync fehlgeschlagen." });
    }
  };

  const runFaciliooContractDebug = async () => {
    const contractId = faciliooDebugContractId.trim();
    if (!contractId) {
      setFaciliooContractTestError("Bitte eine Contract-ID eingeben.");
      setFaciliooContractTestResult(null);
      return;
    }
    setFaciliooContractTestPending(true);
    setFaciliooContractTestError(null);
    setFaciliooContractTestResult(null);
    try {
      const result = await api.getFaciliooContractLive(contractId);
      setFaciliooContractTestResult(result);
    } catch (error) {
      setFaciliooContractTestError(error instanceof Error ? error.message : "Contract-Debug fehlgeschlagen");
    } finally {
      setFaciliooContractTestPending(false);
    }
  };

  const runFaciliooProcessDebug = async () => {
    const processRef = faciliooDebugProcessRef.trim();
    if (!processRef) {
      setFaciliooProcessTestError("Bitte eine Vorgangs-ID oder Vorgangsnummer eingeben.");
      setFaciliooProcessTestResult(null);
      return;
    }
    setFaciliooProcessTestPending(true);
    setFaciliooProcessTestError(null);
    setFaciliooProcessTestResult(null);
    try {
      const result = await api.resolveFaciliooProcess(processRef, {
        postFeedEntry: faciliooProcessFeedTestEnabled,
        feedText: faciliooProcessFeedTestText.trim() || undefined
      });
      setFaciliooProcessTestResult(result);
    } catch (error) {
      setFaciliooProcessTestError(error instanceof Error ? error.message : "Vorgangs-Debug fehlgeschlagen");
    } finally {
      setFaciliooProcessTestPending(false);
    }
  };

  return (
    <div className={isFrameworkV2 ? "resolutions-v2-page debug-v2-page" : undefined}>
      {isFrameworkV2 ? (
        <section className="page-header panel resolutions-v2-page-header debug-v2-page-header">
          <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
            <span className="resolutions-v2-title-icon" aria-hidden="true">{debugTitleIcon}</span>
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Debug</span>
              <span className="resolutions-v2-title-sub">Facilioo-Datenstand, Diagnosewerkzeuge und Synchronisationsdetails</span>
            </span>
          </h2>
        </section>
      ) : null}
      <section className="panel debug-v2-section">
        <h2 style={{ marginTop: 0 }}>Debug | Facilioo Datenstand</h2>
        {faciliooEnabled ? (
          <>
            <div className="kpi-grid" style={{ marginBottom: 12 }}>
              <div className="kpi">
                Facilioo Live-Sync
                <strong>{faciliooSyncStatusData?.enabled ? "Aktiv" : "Inaktiv"}</strong>
                <small style={{ color: "#5a6875" }}>
                  Letzter Lauf: {lastRunLabel}
                </small>
              </div>
              <div className="kpi">
                Einheiten ohne aktive Zuordnung
                <strong>{unitsWithoutActiveAssignment.length}</strong>
                <small style={{ color: "#5a6875" }}>
                  Gesamt relevant: {unitsWithoutActiveAssignment.length}
                  {assignmentAnomalies?.ignoredUnitsWithoutActiveAssignment.length
                    ? ` | Sonstiges/Info: ${assignmentAnomalies.ignoredUnitsWithoutActiveAssignment.length}`
                    : ""}
                </small>
              </div>
              <div className="kpi">
                Mehrfach aktive Zuordnungen
                <strong>{unitsWithMultipleActiveAssignments.length}</strong>
                <small style={{ color: "#5a6875" }}>
                  Gesamt relevant: {unitsWithMultipleActiveAssignments.length}
                </small>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              {datasetStats.map((dataset) => (
                <span key={dataset.key} className="badge" style={{ marginRight: 8, marginBottom: 8 }}>
                  {dataset.label}: geholt {dataset.stats?.fetched ?? 0}, neu {dataset.stats?.created ?? 0}, aktualisiert {dataset.stats?.updated ?? 0}
                </span>
              ))}
            </div>
            {faciliooSyncStatusError || assignmentAnomaliesError ? (
              <div style={{ marginBottom: 8 }}>
                <small style={{ color: "#b42318", fontWeight: 600 }}>
                  Debug-Daten konnten teilweise nicht geladen werden:
                  {` ${faciliooSyncStatusError instanceof Error ? faciliooSyncStatusError.message : ""}`}
                  {` ${assignmentAnomaliesError instanceof Error ? assignmentAnomaliesError.message : ""}`}
                </small>
              </div>
            ) : null}
            <AssignmentAnomalyDebugList
              id="assignment-anomalies-without"
              title="Einheiten ohne aktive Zuordnung"
              units={unitsWithoutActiveAssignment}
              csvFilenamePrefix="debug-einheiten-ohne-aktive-zuordnung"
              emptyText="Keine Einheiten ohne aktive Zuordnung für die aktuelle Suche/Filter gefunden."
              ignoredInfoCount={assignmentAnomalies?.ignoredUnitsWithoutActiveAssignment.length ?? 0}
              hint={(unit) =>
                String(unit.einheitstyp ?? "").trim().toLowerCase() === "sonstiges"
                  ? "Typ Sonstiges: in der Regel ohne Eigentümerzuordnung fachlich korrekt."
                  : "-"
              }
            />
            <AssignmentAnomalyDebugList
              id="assignment-anomalies-multiple"
              title="Einheiten mit mehrfach aktiver Zuordnung"
              units={unitsWithMultipleActiveAssignments}
              csvFilenamePrefix="debug-einheiten-mit-mehrfach-aktiver-zuordnung"
              emptyText="Keine Einheiten mit mehrfach aktiver Zuordnung für die aktuelle Suche/Filter gefunden."
              hint={(unit) => `Aktive Zuordnungen: ${unit.activeAssignmentCount}`}
            />
          </>
        ) : (
          <small style={{ color: "#5a6875" }}>Facilioo ist derzeit global deaktiviert.</small>
        )}
      </section>

      <section className="panel create-form debug-v2-section debug-v2-tools-section">
        <h2 style={{ marginTop: 0 }}>Debug | Facilioo</h2>
        {faciliooEnabled ? (
          <>
            <div className="panel-inset">
              <div className="form-grid">
                <div className="form-field">
                  <label>Debug-Account-ID (optional)</label>
                  <input value={faciliooDebugAccountId} placeholder="z. B. 789448" onChange={(e) => setFaciliooDebugAccountId(e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Debug-Contract-ID (optional)</label>
                  <input value={faciliooDebugContractId} placeholder="z. B. 5764878" onChange={(e) => setFaciliooDebugContractId(e.target.value)} />
                </div>
                <div className="form-field form-field-wide">
                  <label>Debug-Vorgang (ID/Nummer)</label>
                  <input value={faciliooDebugProcessRef} placeholder="z. B. 123456 oder 9876543210" onChange={(e) => setFaciliooDebugProcessRef(e.target.value)} />
                  <label className="result-visibility-switch imports-facilioo-switch" aria-label="Feed-Testeintrag in Vorgangsverlauf schreiben" style={{ marginTop: 8 }}>
                    <input
                      className="result-visibility-input"
                      type="checkbox"
                      checked={faciliooProcessFeedTestEnabled}
                      onChange={(e) => setFaciliooProcessFeedTestEnabled(e.target.checked)}
                    />
                    <span className={`result-visibility-track ${faciliooProcessFeedTestEnabled ? "is-on" : ""}`}>
                      <span className="result-visibility-thumb" />
                    </span>
                    <span>Feed-Testeintrag schreiben</span>
                  </label>
                  {faciliooProcessFeedTestEnabled ? (
                    <textarea
                      value={faciliooProcessFeedTestText}
                      placeholder="Optionaler Feed-Text (leer = automatisch erzeugter Debug-Text)"
                      onChange={(e) => setFaciliooProcessFeedTestText(e.target.value)}
                      rows={3}
                      style={{ width: "100%", marginTop: 8, resize: "vertical" }}
                    />
                  ) : null}
                </div>
              </div>
            </div>
            <div className="form-actions imports-facilioo-actions-row">
              <button disabled={faciliooMasterDataProbePending} onClick={runFaciliooMasterDataProbe}>
                {faciliooMasterDataProbePending ? "Objekte/Einheiten werden gezählt..." : "Objekte/Einheiten zählen"}
              </button>
              <button onClick={runFaciliooContactDebugSyncNow}>Kontakt-Debug (Account)</button>
              <button disabled={faciliooContractTestPending} onClick={runFaciliooContractDebug}>
                {faciliooContractTestPending ? "Contract-Debug läuft..." : "Contract-Debug (Facilioo)"}
              </button>
              <button disabled={faciliooProcessTestPending} onClick={runFaciliooProcessDebug}>
                {faciliooProcessTestPending ? "Vorgangs-Debug läuft..." : "Vorgangs-Debug (Facilioo)"}
              </button>
            </div>
            {faciliooDebugStatus ? (
              <small style={{ color: faciliooDebugStatus.type === "ok" ? "#0f7b31" : "#b42318", fontWeight: 600 }}>{faciliooDebugStatus.text}</small>
            ) : null}
            {faciliooMasterDataProbeError ? (
              <small style={{ color: "#b42318", fontWeight: 600 }}>{faciliooMasterDataProbeError}</small>
            ) : null}
            {faciliooMasterDataProbe ? (
              <div className="panel-inset" style={{ marginTop: 10 }}>
                <strong>Facilioo Master-Data Probe</strong>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  Modus: {faciliooMasterDataProbe.mode === "count" ? "Count" : "Full"} | Dauer: {formatDurationLabel(faciliooMasterDataProbe.durationMs)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13 }}>
                  Objekte: {faciliooMasterDataProbe.objects.totalCount} | Einheiten: {faciliooMasterDataProbe.units.totalCount}
                </div>
              </div>
            ) : null}
            {faciliooContractTestError ? <small style={{ color: "#b42318", fontWeight: 600 }}>{faciliooContractTestError}</small> : null}
            {faciliooContractTestResult ? (
              <div className="panel-inset" style={{ marginTop: 10 }}>
                <strong>Contract-Debug Ergebnis</strong>
                <pre style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{JSON.stringify(faciliooContractTestResult, null, 2)}</pre>
              </div>
            ) : null}
            {faciliooProcessTestError ? <small style={{ color: "#b42318", fontWeight: 600 }}>{faciliooProcessTestError}</small> : null}
            {faciliooProcessTestResult ? (
              <div className="panel-inset" style={{ marginTop: 10 }}>
                <strong>Vorgangs-Debug Ergebnis</strong>
                <pre style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{JSON.stringify(faciliooProcessTestResult, null, 2)}</pre>
              </div>
            ) : null}
          </>
        ) : (
          <small style={{ color: "#5a6875" }}>Facilioo ist derzeit global deaktiviert.</small>
        )}
      </section>
      {faciliooEnabled ? <section className="panel debug-v2-section debug-v2-sync-section">
        <h2 style={{ marginTop: 0 }}>Debug | Facilioo Synchronisationen</h2>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Status">
            <button
              type="button"
              className={syncStatusFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("all");
                setSyncPage(1);
              }}
            >
              Alle Status
            </button>
            <button
              type="button"
              className={syncStatusFilter === "ok" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("ok");
                setSyncPage(1);
              }}
            >
              OK
            </button>
            <button
              type="button"
              className={syncStatusFilter === "error" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("error");
                setSyncPage(1);
              }}
            >
              Fehler
            </button>
          </div>
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Ausführung">
            <button
              type="button"
              className={syncTriggerFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("all");
                setSyncPage(1);
              }}
            >
              Alle Läufe
            </button>
            <button
              type="button"
              className={syncTriggerFilter === "manual" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("manual");
                setSyncPage(1);
              }}
            >
              Manuell
            </button>
            <button
              type="button"
              className={syncTriggerFilter === "auto" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("auto");
                setSyncPage(1);
              }}
            >
              Automatisch
            </button>
          </div>
        </div>
        <div style={{ marginBottom: 8, fontSize: 12, color: "#5a6875" }}>
          Zeilen: {syncRows.length} / Gesamt: {syncTotal}
        </div>
        <div style={{ overflowX: "auto", overflowY: "visible", maxHeight: 420 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Zeitpunkt</th>
                <th style={{ width: 90 }}>Ausführung</th>
                <th style={{ width: 74 }}>Status</th>
                <th style={{ width: 130 }}>Benutzer/System</th>
                <th style={{ width: 90 }}>Lauf-ID</th>
              </tr>
            </thead>
            <tbody>
              {syncRows.reduce<ReactNode[]>((acc, row) => {
                const safeRow: Record<string, unknown> = isRecord(row) ? row : {};
                const action = toText(safeRow.action);
                const rowId = toText(safeRow.id) || `${toText(safeRow.createdAt)}-${action}-${toText(safeRow.entity_id)}`;
                acc.push(
                <tr key={`main-${rowId}`}>
                  <td>{toText(safeRow.createdAt)}</td>
                  <td>{faciliooSyncTrigger(action)}</td>
                  <td>
                <span className={action.endsWith("_failed") ? "badge import-failed" : "badge import-imported"}>
                      {faciliooSyncStatusLabel(action)}
                    </span>
                  </td>
                  <td>{toText(safeRow.actor_name)}</td>
                  <td>{toText(safeRow.entity_id)}</td>
                </tr>,
                <tr key={`debug-${rowId}`}>
                  <td colSpan={5} style={{ paddingTop: 0 }}>
                    {renderFaciliooDebug(row as AuditRow)}
                  </td>
                </tr>
                );
                return acc;
              }, [])}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between" }}>
          <button onClick={() => setSyncPage(Math.max(1, syncPage - 1))} disabled={syncPage <= 1}>
            Zurück
          </button>
          <span style={{ fontSize: 13 }}>
            Seite {syncPage} / {syncTotalPages}
          </span>
          <button onClick={() => setSyncPage(Math.min(syncTotalPages, syncPage + 1))} disabled={syncPage >= syncTotalPages}>
            Weiter
          </button>
        </div>
      </section> : (
        <section className="panel debug-v2-section debug-v2-sync-section">
          <h2 style={{ marginTop: 0 }}>Debug | Facilioo Synchronisationen</h2>
          <small style={{ color: "#5a6875" }}>Facilioo ist derzeit global deaktiviert. Daher werden keine Synchronisations-Logs angezeigt.</small>
        </section>
      )}
    </div>
  );
};

export const AuditPage = () => (<AuditPageContent />);


